import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  AtualizarItemDto, CriarItemDto, ItemResponse, ItensPaginaResponse, ListarItensPaginaQuery, ListarItensQuery,
  ResumoItemResponse, VizinhosItemQuery, VizinhosItemResponse,
} from '@triade/contracts';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

export function useItens(q: Partial<ListarItensQuery> = {}) {
  return useConsulta<ItemResponse[]>(['itens'], '/itens', { query: q });
}

export function useItem(id: string | undefined) {
  return useConsulta<ItemResponse>(['itens', id], `/itens/${id}`, { ativo: !!id });
}

/** Pagina + total real do filtro (GET /itens/pagina), ordenada por codigo. Mantem a pagina anterior enquanto carrega. */
export function useItensPagina(q: Partial<ListarItensPaginaQuery> = {}) {
  const { empresaId } = useSessao();
  return useQuery<ItensPaginaResponse, ApiError>({
    queryKey: ['itens', 'pagina', empresaId, q],
    queryFn: () => requisitar<ItensPaginaResponse>('/itens/pagina', { query: q }),
    enabled: empresaId !== null,
    placeholderData: keepPreviousData,
  });
}

/** Posicao "n de total" e vizinhos do item na lista filtrada (GET /itens/vizinhos). */
export function useVizinhosItem(id: string | undefined, filtro: Omit<VizinhosItemQuery, 'id'> = {}) {
  return useConsulta<VizinhosItemResponse>(['itens', 'vizinhos', id], '/itens/vizinhos', { query: { ...filtro, id }, ativo: !!id });
}

/** Vigentes na data (hoje, no servidor), ficha escolhida e precos praticados recentes (GET /itens/:id/resumo). */
export function useResumoItem(id: string | undefined) {
  return useConsulta<ResumoItemResponse>(['itens', 'resumo', id], `/itens/${id}/resumo`, { ativo: !!id });
}

export function useCriarItem() {
  return useMutacao<ItemResponse, CriarItemDto>({
    metodo: 'POST', caminho: () => '/itens', corpo: (v) => v, invalidar: [['itens']],
  });
}

export function useAtualizarItem() {
  return useMutacao<ItemResponse, { id: string } & AtualizarItemDto>({
    metodo: 'PATCH',
    caminho: (v) => `/itens/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['itens']],
  });
}

/** DELETE /itens/:id: a API arquiva (soft delete), nao apaga. */
export function useArquivarItem() {
  return useMutacao<void, { id: string }>({
    metodo: 'DELETE', caminho: (v) => `/itens/${v.id}`, invalidar: [['itens']],
  });
}
